import { supabase } from "./supabase";
import type {
  AccountEntityRole,
  AccountMarketProfile,
  AccountBrandGuidance,
  EntityRole,
  ScoreTreatment,
} from "../types/competitiveSetup";

async function fetchCompetitiveSetupSnapshot(accountId: string) {
  const { data: sessionData } = await supabase.auth.getSession();

  const response = await fetch(
    `/api/admin-onboarding-competitive-setup-snapshot?account_id=${encodeURIComponent(accountId)}`,
    {
      method: "GET",
      headers: {
        Authorization: `Bearer ${sessionData.session?.access_token || ""}`,
      },
    }
  );

  const text = await response.text();

  let payload: any = null;
  try {
    payload = text ? JSON.parse(text) : null;
  } catch {
    payload = {
      error: "Competitive setup snapshot returned a non-JSON response",
      status: response.status,
      body_preview: text.slice(0, 500),
    };
  }

  if (!response.ok || !payload?.ok) {
    throw new Error(payload?.error || `Competitive setup snapshot failed with status ${response.status}`);
  }

  return payload;
}

export async function getCompetitiveSetupSnapshot(accountId: string) {
  return fetchCompetitiveSetupSnapshot(accountId);
}


export async function getLatestMarketProfile(accountId: string) {
  const snapshot = await fetchCompetitiveSetupSnapshot(accountId);
  return snapshot.market_profile as AccountMarketProfile | null;
}

export async function getEntityRoles(accountId: string) {
  const snapshot = await fetchCompetitiveSetupSnapshot(accountId);
  return (snapshot.entity_roles || []) as AccountEntityRole[];
}


export async function getLatestBrandGuidance(accountId: string) {
  const snapshot = await fetchCompetitiveSetupSnapshot(accountId);
  return snapshot.brand_guidance as AccountBrandGuidance | null;
}

export async function saveBrandGuidance(input: {
  guidanceId?: string | null;
  account_id: string;
  status?: string;
  guidance_title?: string | null;
  raw_guidance?: string | null;
  brand_voice_summary?: string | null;
  tone_do?: string[];
  tone_dont?: string[];
  preferred_terms?: string[];
  banned_terms?: string[];
  approved_claims?: string[];
  claims_to_avoid?: string[];
  audience_notes?: string | null;
  positioning_notes?: string | null;
  differentiators?: string[];
  cta_preferences?: string | null;
  compliance_notes?: string | null;
  oe_guardrails?: string | null;
  prompt_generation_notes?: string | null;
  market_profile_notes?: string | null;
  apply_to_opportunity_engine?: boolean;
  apply_to_prompt_generation?: boolean;
  apply_to_market_profile?: boolean;
  admin_notes?: string | null;
}) {
  const payload = {
    account_id: input.account_id,
    status: input.status || "draft",
    source: "manual_admin",
    guidance_title: input.guidance_title || null,
    raw_guidance: input.raw_guidance || null,
    brand_voice_summary: input.brand_voice_summary || null,
    tone_do: input.tone_do || [],
    tone_dont: input.tone_dont || [],
    preferred_terms: input.preferred_terms || [],
    banned_terms: input.banned_terms || [],
    approved_claims: input.approved_claims || [],
    claims_to_avoid: input.claims_to_avoid || [],
    audience_notes: input.audience_notes || null,
    positioning_notes: input.positioning_notes || null,
    differentiators: input.differentiators || [],
    cta_preferences: input.cta_preferences || null,
    compliance_notes: input.compliance_notes || null,
    oe_guardrails: input.oe_guardrails || null,
    prompt_generation_notes: input.prompt_generation_notes || null,
    market_profile_notes: input.market_profile_notes || null,
    apply_to_opportunity_engine: input.apply_to_opportunity_engine ?? true,
    apply_to_prompt_generation: input.apply_to_prompt_generation ?? true,
    apply_to_market_profile: input.apply_to_market_profile ?? true,
    admin_notes: input.admin_notes || null,
  };

  if (input.guidanceId) {
    const { data, error } = await supabase
      .from("account_brand_guidance")
      .update(payload)
      .eq("id", input.guidanceId)
      .select("*")
      .single();

    if (error) throw error;
    return data as AccountBrandGuidance;
  }

  const { data, error } = await supabase
    .from("account_brand_guidance")
    .insert(payload)
    .select("*")
    .single();

  if (error) throw error;
  return data as AccountBrandGuidance;
}

async function callAdminMarketProfileApi(method: "POST" | "PATCH", body: Record<string, any>) {
  const { data: sessionData } = await supabase.auth.getSession();

  const response = await fetch("/api/admin-onboarding-market-profile", {
    method,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${sessionData.session?.access_token || ""}`,
    },
    body: JSON.stringify(body),
  });

  const text = await response.text();

  let payload: any = null;
  try {
    payload = text ? JSON.parse(text) : null;
  } catch {
    payload = {
      error: "Upstream returned a non-JSON response",
      status: response.status,
      body_preview: text.slice(0, 500),
    };
  }

  if (!response.ok) {
    throw new Error(payload?.error || `Market profile API failed with status ${response.status}`);
  }

  return payload;
}

export async function saveMarketProfile(input: {
  profileId?: string | null;
  account_id: string;
  status?: "draft" | "agent_generated" | "needs_review" | "approved" | "rejected" | "superseded";
  primary_category?: string | null;
  category_description?: string | null;
  plain_language_description?: string | null;
  buyer_types?: string[];
  jobs_to_be_done?: string[];
  direct_competitor_definition?: string | null;
  adjacent_competitor_definition?: string | null;
  platform_partner_definition?: string | null;
  exclusion_definition?: string | null;
  known_direct_competitors?: string[];
  known_adjacent_competitors?: string[];
  known_platforms_partners?: string[];
  known_exclusions?: string[];
  confidence_score?: number | null;
  admin_notes?: string | null;
}) {
  const payload = await callAdminMarketProfileApi(input.profileId ? "PATCH" : "POST", {
    action: "save",
    ...input,
  });

  return payload.profile as AccountMarketProfile;
}

export async function updateMarketProfileStatus(input: {
  profileId: string;
  account_id: string;
  status: "draft" | "needs_review" | "approved" | "rejected" | "superseded";
}) {
  const payload = await callAdminMarketProfileApi("PATCH", {
    action: "status",
    ...input,
  });

  return payload.profile as AccountMarketProfile;
}

export async function createEntityRole(input: {
  account_id: string;
  market_profile_id?: string | null;
  raw_entity_name: string;
  normalized_entity_name: string;
  parent_entity_name?: string | null;
  entity_role: EntityRole;
  score_treatment: ScoreTreatment;
  confidence_score?: number | null;
  rationale?: string | null;
  admin_notes?: string | null;
}) {
  const { data, error } = await supabase
    .from("account_entity_roles")
    .insert({
      ...input,
      source: "manual_admin",
      approved_at: new Date().toISOString(),
    })
    .select("*")
    .single();

  if (error) throw error;
  return data as AccountEntityRole;
}

export async function updateEntityRole(
  id: string,
  patch: Partial<Pick<
    AccountEntityRole,
    | "raw_entity_name"
    | "normalized_entity_name"
    | "parent_entity_name"
    | "entity_role"
    | "score_treatment"
    | "confidence_score"
    | "rationale"
    | "admin_notes"
    | "is_active"
  >>
) {
  const { data, error } = await supabase
    .from("account_entity_roles")
    .update(patch)
    .eq("id", id)
    .select("*")
    .single();

  if (error) throw error;
  return data as AccountEntityRole;
}

async function parseAdminApiResponse(response: Response, fallbackMessage: string) {
  const contentType = response.headers.get("content-type") || "";
  const rawText = await response.text();

  let payload: any = null;

  if (contentType.includes("application/json") && rawText) {
    try {
      payload = JSON.parse(rawText);
    } catch {
      payload = null;
    }
  }

  if (!response.ok || !payload?.ok) {
    const upstreamHint = rawText
      ? rawText.slice(0, 300).replace(/\s+/g, " ").trim()
      : "";

    throw new Error(
      payload?.error ||
        `${fallbackMessage}. Status ${response.status}${
          upstreamHint ? `: ${upstreamHint}` : ""
        }`
    );
  }

  return payload;
}

export async function generateMarketProfileForAccount(accountId: string) {
  const { data: sessionData } = await supabase.auth.getSession();
  const response = await fetch("/api/admin-generate-market-profile", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${sessionData.session?.access_token || ""}`,
    },
    body: JSON.stringify({ accountId }),
  });

  const payload = await parseAdminApiResponse(
    response,
    "Failed to generate market profile"
  );

  return payload.profile;
}

export async function generateEntityRoleSeedsForAccount(
  accountId: string,
  marketProfileId?: string | null
) {
  const { data: sessionData } = await supabase.auth.getSession();
  const response = await fetch("/api/admin-generate-entity-role-seeds", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${sessionData.session?.access_token || ""}`,
    },
    body: JSON.stringify({ accountId, marketProfileId }),
  });

  const payload = await parseAdminApiResponse(
    response,
    "Failed to generate entity role seeds"
  );

  return payload;
}
